import { useEffect, useState, useCallback } from "react";
import { fmtDateTime } from "@/components/DatePicker";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { fmtDate } from "@/components/DatePicker";
import api, { API, usd, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/PhoneInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { OfferLetterhead, OfferPaper } from "@/components/OfferLetterhead";
import { RichTextEditor } from "@/components/RichTextEditor";
import { OfferPill } from "@/pages/Hiring";
import { ArrowLeft, Save, Download, Send, Printer, Copy, CheckCircle2, XCircle, Eye, Pencil, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function OfferLetterEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [html, setHtml] = useState("");
  const [cand, setCand] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "edit" ? "edit" : "view";
  const created = params.get("created") === "1";
  const setMode = (m) => { const p = new URLSearchParams(params); p.set("mode", m); p.delete("created"); setParams(p, { replace: true }); };
  const saveMeta = async (patch) => {
    try { const { data } = await api.put(`/offer-letters/${id}`, patch); setO(data); toast.success("Letterhead updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const hire = async () => {
    if (!window.confirm(`Create the employee record for ${o.candidate?.name} and send the W-4 / I-9 / direct deposit onboarding now?`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/offer-letters/${id}/hire`); toast.success(data.already ? "Onboarding was already started for this candidate" : "Employee created — onboarding email sent"); navigate("/hiring?tab=hires"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const onLogoFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 300 * 1024) return toast.error("Logo must be under 300 KB");
    const r = new FileReader(); r.onload = () => saveMeta({ letterhead_logo: r.result }); r.readAsDataURL(file); e.target.value = "";
  };
  useEffect(() => { if (o && params.get("print") === "1") { const p = new URLSearchParams(params); p.delete("print"); setParams(p, { replace: true }); setTimeout(() => window.print(), 600); } }, [o]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => api.get(`/offer-letters/${id}`).then((r) => { setO(r.data); setHtml(r.data.html); setCand(r.data.candidate); setDirty(false); }).catch(() => toast.error("Offer not found")), [id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try { await api.put(`/offer-letters/${id}`, { html, candidate: cand }); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const send = async () => {
    if (dirty) await save();
    if (!cand.email) return toast.error("Add the candidate's email first");
    setBusy(true);
    try { await api.post(`/offer-letters/${id}/send`); toast.success(`Offer emailed to ${cand.email}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const download = async () => {
    try {
      const res = await api.get(`/offer-letters/${id}/docx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `Offer_${cand.name?.replace(/\s+/g, "_")}.docx`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!o) return <div className="text-muted-foreground">Loading…</div>;
  const signed = ["accepted", "declined"].includes(o.status);

  return (
    <div className="space-y-5 animate-fade-up" data-testid="offer-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/hiring")} data-testid="offer-back"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">Offer · {o.candidate?.name} <OfferPill status={o.status} /></h1>
            <p className="text-sm text-muted-foreground mt-1">{o.job_title} at {o.company_name} · starts {fmtDate(o.start_date)} · {usd(o.salary)}{o.pay_type === "hourly" ? "/hr" : "/yr"}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {mode === "edit" ? <Button variant="outline" data-testid="offer-view-btn" onClick={() => setMode("view")}><Eye className="w-4 h-4 mr-1.5" /> View Offer</Button>
            : !signed && <Button variant="outline" data-testid="offer-edit-btn" onClick={() => setMode("edit")}><Pencil className="w-4 h-4 mr-1.5" /> Edit Offer</Button>}
          <Button variant="outline" data-testid="offer-print" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1.5" /> Print Offer</Button>
          <Button variant="outline" data-testid="offer-docx" onClick={download}><Download className="w-4 h-4 mr-1.5" /> Word (.docx)</Button>
          {!signed && mode === "edit" && <Button variant="outline" data-testid="offer-save" onClick={save} disabled={busy || !dirty}><Save className="w-4 h-4 mr-1.5" /> Save</Button>}
          {!signed && <Button data-testid="offer-send" onClick={send} disabled={busy}><Send className="w-4 h-4 mr-1.5" /> {o.status === "sent" ? "Re-email Offer" : "Email Offer"}</Button>}
          {!o.hired_at && (o.status === "accepted" || o.status === "sent" || o.status === "draft") && <Button variant="outline" className="border-emerald-300 text-emerald-700" data-testid="offer-hire" disabled={busy} onClick={hire}><UserPlus className="w-4 h-4 mr-1.5" /> Hire & start onboarding</Button>}
        </div>
      </div>
      {created && (
        <Card className="p-4 border-emerald-200 bg-emerald-50/60 dark:bg-emerald-500/10 flex items-center gap-3 flex-wrap print:hidden" data-testid="offer-created-banner">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" /><span className="text-sm font-medium flex-1">Offer letter created for {o.candidate?.name}. What would you like to do next?</span>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" data-testid="created-view" onClick={() => setMode("view")}><Eye className="w-4 h-4 mr-1" /> View</Button>
            <Button size="sm" variant="outline" data-testid="created-edit" onClick={() => setMode("edit")}><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
            <Button size="sm" variant="outline" data-testid="created-print" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Print</Button>
            <Button size="sm" data-testid="created-email" onClick={send} disabled={busy}><Send className="w-4 h-4 mr-1" /> Email Offer</Button>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        <div className="print:col-span-2 print-area">
          <OfferPaper>
            <OfferLetterhead company={o.company} showLogo={o.show_logo !== false} logoOverride={o.letterhead_logo} offerDate={o.offer_date} candidate={o.candidate} status={o.status} />
            <div className="mt-8">
              {mode === "edit" && !signed
                ? <div className="font-sans not-prose"><RichTextEditor value={html} readOnly={false} onChange={(v) => { setHtml(v); setDirty(true); }} /></div>
                : <div data-testid="offer-view" dangerouslySetInnerHTML={{ __html: html }} />}
            </div>
            {signed && o.signed && (
              <div className="mt-10 pt-6 border-t border-slate-200 grid sm:grid-cols-2 gap-6 text-sm" data-testid="offer-signature-block">
                <div>{o.signed.signature && <img src={o.signed.signature} alt="signature" className="h-14 object-contain" />}<div className="border-t border-slate-400 mt-1 pt-1 text-xs text-slate-500">Candidate signature · {o.signed.name} · {fmtDate((o.signed.at || "").slice(0, 10))}</div></div>
                <div className="self-end text-xs text-slate-500">Signed electronically via RightPay · IP {o.signed.ip || "—"}</div>
              </div>
            )}
          </OfferPaper>
          {o.signed && (
            <Card className="mt-4 p-4 flex items-center gap-4" data-testid="offer-signature-block">
              {o.signed.signature && <img src={o.signed.signature} alt="signature" className="h-16 bg-white rounded border border-border px-2" />}
              <div className="text-sm">
                <div className={`font-semibold flex items-center gap-1.5 ${o.status === "accepted" ? "text-emerald-600" : "text-rose-600"}`}>{o.status === "accepted" ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {o.status === "accepted" ? "Accepted" : "Declined"} by {o.signed.name}</div>
                <div className="text-xs text-muted-foreground">{fmtDateTime(o.signed.at)} · IP {o.signed.ip}</div>
                {o.signed.note && <div className="text-xs mt-1">“{o.signed.note}”</div>}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-4 print:hidden">
          <Card className="p-4 space-y-3 print:hidden" data-testid="offer-candidate-panel">
            <div className="rounded-lg border p-3 space-y-2" data-testid="offer-letterhead-settings">
              <div className="flex items-center justify-between gap-2">
                <div><Label htmlFor="offer-show-logo" className="text-sm">Logo on letterhead</Label><p className="text-[11px] text-muted-foreground">Uses the client logo unless you upload one for this offer.</p></div>
                <Switch id="offer-show-logo" data-testid="offer-show-logo" checked={o.show_logo !== false} disabled={signed} onCheckedChange={(v) => saveMeta({ show_logo: v })} />
              </div>
              {!signed && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-primary hover:underline cursor-pointer" data-testid="offer-logo-upload-label">
                    {o.letterhead_logo ? "Replace logo for this offer" : "Upload a logo for this offer"}
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" data-testid="offer-logo-upload" onChange={onLogoFile} />
                  </label>
                  {o.letterhead_logo && <button type="button" data-testid="offer-logo-clear" onClick={() => saveMeta({ letterhead_logo: "" })} className="text-xs text-muted-foreground hover:text-rose-600">Use client logo instead</button>}
                </div>
              )}
            </div>
            <div className="font-display font-semibold">Candidate</div>
            <div className="space-y-1"><Label className="text-xs">Full name</Label><Input data-testid="offer-cand-name" value={cand.name || ""} disabled={signed} onChange={(e) => { setCand({ ...cand, name: e.target.value }); setDirty(true); }} /></div>
            <div className="space-y-1"><Label className="text-xs">Email (for e-signature)</Label><Input data-testid="offer-cand-email" type="email" value={cand.email || ""} disabled={signed} onChange={(e) => { setCand({ ...cand, email: e.target.value }); setDirty(true); }} /></div>
            <div className="space-y-1"><Label className="text-xs">Phone</Label><PhoneInput data-testid="offer-cand-phone" value={cand.phone || ""} disabled={signed} onChange={(v) => { setCand({ ...cand, phone: v }); setDirty(true); }} /></div>
          </Card>
          <Card className="p-4 space-y-2" data-testid="offer-sign-link">
            <div className="font-display font-semibold">E-signature link</div>
            <p className="text-xs text-muted-foreground">Emailed automatically when you send. You can also share it directly.</p>
            <Button size="sm" variant="outline" className="w-full" data-testid="offer-copy-link" onClick={() => { navigator.clipboard.writeText(o.sign_url); toast.success("Signing link copied"); }}><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy signing link</Button>
          </Card>
          <Card className="p-4" data-testid="offer-timeline">
            <div className="font-display font-semibold mb-3">Activity</div>
            <ol className="space-y-3">{[...(o.timeline || [])].reverse().map((t, i) => (
              <li key={i} className="flex gap-3 text-sm"><span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" /><div><div className="font-medium">{t.event}</div><div className="text-xs text-muted-foreground">{t.by} · {fmtDateTime(t.at)}</div></div></li>
            ))}</ol>
          </Card>
        </div>
      </div>
    </div>
  );
}
